import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Menu, X, ChevronDown, User, LayoutDashboard, Settings,
  Receipt, Image as ImageIcon, CreditCard, Lock, LogOut, Smartphone, Palette,
} from 'lucide-react';

function BrandMark() {
  return (
    <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
      <img
        src="/ck-logo-v2.png"
        alt="ChitraKalakar"
        className="h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
      />
      <div className="hidden sm:block leading-none">
        <h1 className="font-serif text-xl font-semibold tracking-[0.06em]">
          <span className="text-navy-800">CHITRA</span>
          <span className="text-gold-600">KALAKAR</span>
        </h1>
        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-600/80">
          Art · Inspiration · Infinite
        </p>
      </div>
    </Link>
  );
}

function NavBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { isAuthenticated, user, profiles, logout, isAdmin, isArtist } = useAuth();
  const navigate = useNavigate();
  const userDropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    setShowUserDropdown(false);
    navigate('/');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'About', href: '/about' },
    { label: 'Paintings', href: '/paintings' },
    { label: 'Artists', href: '/artists' },
    { label: 'Exhibitions', href: '/exhibitions' },
    { label: 'Communities', href: '/communities' },
    { label: 'Contact', href: '/contact' },
    { label: 'Art Classes', href: '/art-classes' },
  ];

  const getDashboardLink = () => {
    if (isAdmin) return { href: '/admin', label: 'Admin Dashboard' };
    if (profiles?.role === 'lead_chitrakar') return { href: '/lead-chitrakar', label: 'Lead Chitrakar' };
    if (profiles?.role === 'kalakar') return { href: '/kalakar', label: 'Kalakar Panel' };
    if (isArtist) return { href: '/dashboard', label: 'Artist Dashboard' };
    return { href: '/user-dashboard', label: 'My Dashboard' };
  };

  const getUserMenuItems = () => {
    const items = [
      { label: 'Profile', href: '/profile', Icon: User },
      { label: getDashboardLink().label, href: getDashboardLink().href, Icon: LayoutDashboard },
      { label: 'Account Settings', href: '/account', Icon: Settings },
    ];
    if (isAdmin) {
      items.push({ label: 'Artist Studio', href: '/dashboard', Icon: Palette });
      items.push({ label: 'Commission Requests', href: '/admin/commissions', Icon: Receipt });
      items.push({ label: 'Exhibitions', href: '/admin/exhibitions', Icon: ImageIcon });
    } else if (isArtist) {
      items.push({ label: 'Commission Queue', href: '/dashboard/commissions', Icon: Receipt });
      items.push({ label: 'Exhibition Requests', href: '/dashboard/exhibitions', Icon: ImageIcon });
      items.push({ label: 'Subscription', href: '/subscription', Icon: CreditCard });
    } else {
      items.push({ label: 'My Commissions', href: '/user-dashboard/commissions', Icon: Receipt });
    }
    items.push({ label: 'Change Password', href: '/change-password', Icon: Lock });
    return items;
  };

  const getAvatarInitial = () => {
    const name = profiles?.full_name || user?.name || 'U';
    return name.charAt(0).toUpperCase();
  };

  const roleLabel = isAdmin ? 'Admin'
    : profiles?.role === 'lead_chitrakar' ? 'Lead Chitrakar'
    : profiles?.role === 'kalakar' ? 'Kalakar'
    : isArtist ? 'Artist' : 'User';

  return (
    <nav className="sticky top-0 z-50 bg-cream-100/85 backdrop-blur-md border-b border-gold-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-[72px]">
          <BrandMark />

          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="relative text-sm font-medium text-navy-700 hover:text-gold-600 transition-colors duration-200 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold-500 after:transition-all after:duration-300 hover:after:w-full"
                data-testid={link.href === '/exhibitions' ? 'navbar-exhibitions-active-link' : `nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-navy-800/5 transition-colors"
                  data-testid="user-menu-button"
                >
                  <div className="w-10 h-10 rounded-full bg-navy-800 flex items-center justify-center text-gold-400 font-semibold text-sm overflow-hidden ring-2 ring-gold-500/30">
                    {profiles?.avatar ? (
                      <img src={profiles.avatar} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      getAvatarInitial()
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-navy-500 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showUserDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-[0_10px_30px_rgba(10,17,40,0.12)] border border-gold-500/15 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-navy-800 flex items-center justify-center text-gold-400 font-semibold overflow-hidden">
                          {profiles?.avatar ? (
                            <img src={profiles.avatar} alt="Avatar" className="w-full h-full object-cover" />
                          ) : (
                            getAvatarInitial()
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-navy-800 truncate">
                            {profiles?.full_name || user?.name || 'User'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{profiles?.email || user?.email}</p>
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-gold-700 bg-gold-500/12 border border-gold-500/20">
                            {roleLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="py-2">
                      {getUserMenuItems().map((item) => (
                        <Link
                          key={item.href + item.label}
                          to={item.href}
                          onClick={() => setShowUserDropdown(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-gold-500/8 hover:text-gold-700 transition-colors"
                          data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        >
                          <item.Icon className="w-4 h-4" strokeWidth={1.6} />
                          {item.label}
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 pt-2">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        data-testid="logout-button"
                      >
                        <LogOut className="w-4 h-4" strokeWidth={1.6} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn-navy-outline !px-6 !py-2 text-sm" data-testid="navbar-login-btn">
                  Login
                </Link>
                <Link to="/signup" className="btn-gold !px-6 !py-2 text-sm" data-testid="navbar-signup-btn">
                  Get Started
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-navy-800 hover:bg-navy-800/5"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="lg:hidden border-t border-gold-500/15 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link key={link.href} to={link.href} onClick={() => setIsOpen(false)} className="block px-3 py-2 text-base font-medium text-navy-700 hover:bg-gold-500/8 hover:text-gold-700 rounded-md">
                {link.label}
              </Link>
            ))}

            <div className="pt-2 space-y-2 border-t border-gray-100">
              <button
                onClick={() => {
                  if (window.deferredPrompt) { window.deferredPrompt.prompt(); }
                  else { alert('To install: Open browser menu → "Add to Home Screen" or "Install App"'); }
                  setIsOpen(false);
                }}
                className="flex items-center gap-3 w-full px-3 py-2 text-base font-medium text-navy-700 bg-cream-200 hover:bg-gold-500/10 rounded-md"
              >
                <Smartphone className="w-5 h-5" strokeWidth={1.6} />
                Install App
              </button>

              {isAuthenticated ? (
                <>
                  <div className="px-3 py-3 bg-cream-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-navy-800 flex items-center justify-center text-gold-400 font-semibold overflow-hidden">
                        {profiles?.avatar ? (
                          <img src={profiles.avatar} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                          getAvatarInitial()
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-navy-800">{profiles?.full_name || user?.name}</p>
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-gold-700 bg-gold-500/12">
                          {roleLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                  {getUserMenuItems().map((item) => (
                    <Link key={item.href + item.label} to={item.href} onClick={() => setIsOpen(false)} className="flex items-center gap-3 px-3 py-2 text-base font-medium text-navy-700 hover:bg-gold-500/8 rounded-md">
                      <item.Icon className="w-5 h-5" strokeWidth={1.6} />
                      {item.label}
                    </Link>
                  ))}
                  <button onClick={handleLogout} className="flex items-center gap-3 w-full px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md">
                    <LogOut className="w-5 h-5" strokeWidth={1.6} />
                    Logout
                  </button>
                </>
              ) : (
                <div className="space-y-2 px-1">
                  <Link to="/login" onClick={() => setIsOpen(false)} className="btn-navy-outline w-full !py-2.5" data-testid="mobile-login-btn">
                    Login
                  </Link>
                  <Link to="/signup" onClick={() => setIsOpen(false)} className="btn-gold w-full !py-2.5" data-testid="mobile-signup-btn">
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default NavBar;
